import { isPromise } from '../isPromise';
import z from 'zod';

export type ZodReportableIssue = z.core.$ZodIssue | z.core.$ZodRawIssue;

export type ZodReportedIssue = {
    issues: ZodReportableIssue[];
    value: unknown;
} & (
    | {
          source: 'forgivingSchema';
          path: string[];
      }
    | {
          source: 'fallback';
      }
);

export type ZodIssueReporter = (report: ZodReportedIssue) => void;

let issueReporter: ZodIssueReporter = () => {};

/**
 * Set the issue reporter used by `zodFallback`, `zodForgivingSchema` and `zodForgivingParse`
 * @param reporter Callback to report issues to
 */
export function setZodIssueReporter(reporter: ZodIssueReporter) {
    issueReporter = reporter;
}

/**
 * Temporarily overrides the issue reporter used by `zodFallback`, `zodForgivingSchema` and `zodForgivingParse`
 * @param reporter Callback to report issues to
 * @param fn Function during which the reporter will be overridden
 */
export function withZodIssueReporter(reporter: ZodIssueReporter, fn: () => void): void;
export function withZodIssueReporter(reporter: ZodIssueReporter, fn: () => Promise<void>): Promise<void>;
export function withZodIssueReporter(reporter: ZodIssueReporter, fn: () => unknown) {
    const previousReporter = issueReporter;
    setZodIssueReporter(reporter);
    const result = fn();
    if (isPromise(result)) {
        return result.then(() => {
            setZodIssueReporter(previousReporter);
        });
    }

    setZodIssueReporter(previousReporter);
}

/**
 * Report issue to currently registered issue reporter
 * @internal
 * @param source Function reporting the issue
 * @param issues Issues reported by Zod
 * @param value Value causing the issues
 * @param path Path of value inside parsed object
 */
export function zodReportIssue(source: ZodReportedIssue['source'], ctx: z.core.$ZodCatchCtx, path: string[] = []) {
    const value = ctx.value;
    // ctx.error.issues has more details, but is deprecated -> use if still possible
    let issues: ZodReportableIssue[] = ctx.issues;
    if ('error' in ctx && 'issues' in ctx.error && Array.isArray(ctx.error.issues)) {
        issues = ctx.error.issues;
    }

    switch (source) {
        case 'forgivingSchema': {
            issueReporter({ source, issues, value, path });
            break;
        }
        default: {
            issueReporter({ source, issues, value });
        }
    }
}
